import * as THREE from 'three';
import { PointerLockControls } from 'three/examples/jsm/controls/PointerLockControls.js';
import { Config } from '../core/Config';
import { Input } from '../core/Input';
import { Crosshair } from '../ui/Crosshair';

export class FPSCamera {
  private camera: THREE.PerspectiveCamera;
  private controls: PointerLockControls;
  private input: Input;
  private velocity: THREE.Vector3 = new THREE.Vector3();
  private direction: THREE.Vector3 = new THREE.Vector3();
  private isEnabled: boolean = false;
  private isRunning: boolean = false;
  private isJumping: boolean = false;
  private canJump: boolean = true;
  private playerHeight: number = Config.FPS_CAMERA.PLAYER_HEIGHT;
  private headBobTimer: number = 0;
  private headBobAmount: number = Config.FPS_CAMERA.HEAD_BOB_AMOUNT;
  private headBobSpeed: number = Config.FPS_CAMERA.HEAD_BOB_SPEED;
  private runningHeadBobAmount: number = Config.FPS_CAMERA.RUNNING_HEAD_BOB_AMOUNT;
  private runningHeadBobSpeed: number = Config.FPS_CAMERA.RUNNING_HEAD_BOB_SPEED;
  private movementSpeed: number = Config.FPS_CAMERA.WALK_SPEED;
  private runningSpeed: number = Config.FPS_CAMERA.RUN_SPEED;
  private jumpVelocity: number = Config.FPS_CAMERA.JUMP_VELOCITY;
  private gravity: number = Config.FPS_CAMERA.GRAVITY;
  private crosshair: Crosshair;
  private deltaTime: number = 0;
  private walkSpeed: number = Config.FPS_CAMERA.WALK_SPEED;
  private runSpeed: number = Config.FPS_CAMERA.RUN_SPEED;
  private moveSpeed: number = Config.FPS_CAMERA.WALK_SPEED;
  private collisions = { bottom: true, top: false, left: false, right: false };
  
  constructor(camera: THREE.PerspectiveCamera, input: Input) {
    this.camera = camera;
    this.controls = new PointerLockControls(this.camera, document.body);
    this.input = input;
    
    // Initialize crosshair
    this.crosshair = new Crosshair();
    
    // Add event listener to lock/unlock controls
    document.addEventListener('click', () => {
      if (this.isEnabled && !this.controls.isLocked) {
        try {
          this.controls.lock();
        } catch (e) {
          console.error('Could not lock pointer on click:', e);
        }
      }
    });
    
    // Add event listener for keydown events
    document.addEventListener('keydown', (event) => {
      // Handle Escape key
      if (event.key === 'Escape') {
        this.controls.unlock();
      }
      
      // Handle Space key for jumping
      if ((event.key === ' ' || event.code === 'Space') && 
          this.canJump && this.isEnabled && (this.controls.isLocked || document.pointerLockElement)) {
        this.velocity.y = this.jumpVelocity;
        this.isJumping = true;
        this.canJump = false;
      }
    });
    
    // Handle pointer lock change to show/hide crosshair
    document.addEventListener('pointerlockchange', () => {
      if (document.pointerLockElement) {
        this.crosshair.show();
      } else {
        this.crosshair.hide();
      }
    });
    
    // Handle pointer lock errors
    document.addEventListener('pointerlockerror', (event) => {
      console.error('Pointer lock error:', event);
      // Fall back to non-pointer lock controls
      this.crosshair.show(); // Still show crosshair
    });
    
    console.log('FPSCamera initialized');
  }
  
  public enable(): void {
    this.isEnabled = true;
    
    // Show crosshair
    this.crosshair.show();
    
    // Try to lock pointer immediately
    try {
      // Check if pointer lock is available in this browser
      if ('pointerLockElement' in document) {
        setTimeout(() => this.controls.lock(), 100); // Short delay to ensure DOM is ready
      } else {
        console.warn('Pointer Lock API not supported in this browser');
      }
    } catch (e) {
      console.error('Could not lock pointer automatically, click required:', e);
    }
  }
  
  public disable(): void {
    this.isEnabled = false;
    this.controls.unlock();
    
    // Hide crosshair
    this.crosshair.hide();
  }
  
  public update(deltaTime: number): void {
    if (!this.isEnabled) {
      return;
    }

    this.deltaTime = deltaTime;
    
    // Check for running (shift key held)
    const isRunning = this.input.isKeyHeld('ShiftLeft') || this.input.isKeyHeld('shift');
    this.moveSpeed = isRunning ? this.runSpeed : this.walkSpeed;
    this.isRunning = isRunning;
    
    // Get movement directions - support both traditional WASD and arrow keys
    const forward = this.input.isKeyHeld('KeyW') || this.input.isKeyHeld('w') || this.input.isKeyHeld('ArrowUp');
    const backward = this.input.isKeyHeld('KeyS') || this.input.isKeyHeld('s') || this.input.isKeyHeld('ArrowDown');
    const left = this.input.isKeyHeld('KeyA') || this.input.isKeyHeld('a') || this.input.isKeyHeld('ArrowLeft');
    const right = this.input.isKeyHeld('KeyD') || this.input.isKeyHeld('d') || this.input.isKeyHeld('ArrowRight');
    
    // Calculate movement vector
    this.direction.z = Number(forward) - Number(backward);
    this.direction.x = Number(right) - Number(left);
    
    // Only normalize if there's movement to avoid division by zero
    if (this.direction.x !== 0 || this.direction.z !== 0) {
      this.direction.normalize(); // Normalize to avoid faster diagonal movement
    }
    
    // Handle movement even when pointer is not locked (for testing)
    if ((this.controls.isLocked || document.pointerLockElement) || true) {
      if (forward || backward) this.moveForward(this.direction.z * this.moveSpeed * deltaTime);
      if (left || right) this.moveRight(this.direction.x * this.moveSpeed * deltaTime);
    }

    // Apply gravity
    if (!this.collisions.bottom) {
      this.velocity.y -= this.gravity * deltaTime * 1.5; // Increased gravity factor for better feel
    } else if (this.velocity.y < 0) {
      // Only reset jump state if velocity is low and on ground
      this.velocity.y = 0;
      if (this.isJumping && Math.abs(this.velocity.y) < 0.1) {
        this.isJumping = false;
        this.canJump = true;
      }
    }
    
    // Update position based on velocity
    this.moveUp(this.velocity.y * deltaTime);
    
    // Update collisions
    this.updateCollisions();
    
    // Head bobbing effect when moving
    this.updateHeadBob(deltaTime);
  }
  
  private updateHeadBob(deltaTime: number): void {
    const isMoving = this.direction.x !== 0 || this.direction.z !== 0;
    
    if (isMoving && !this.isJumping) {
      // Increment the head bob timer
      this.headBobTimer += deltaTime * (this.isRunning ? this.runningHeadBobSpeed : this.headBobSpeed);
      
      // Calculate the bob amount based on whether we're running or walking
      const bobAmount = this.isRunning ? this.runningHeadBobAmount : this.headBobAmount;
      
      // Apply a sine wave to the camera's y position for bobbing
      const offset = Math.sin(this.headBobTimer) * bobAmount;
      
      // Apply to camera position, maintaining the player height
      this.camera.position.y = this.playerHeight + offset;
    } else {
      // Reset the timer when not moving
      this.headBobTimer = 0;
    }
  }
  
  public isActive(): boolean {
    // Check if FPSCamera is enabled
    return this.isEnabled;
  }
  
  public getControls(): PointerLockControls {
    return this.controls;
  }
  
  private moveForward(distance: number): void {
    this.controls.moveForward(distance);
  }
  
  private moveRight(distance: number): void {
    this.controls.moveRight(distance);
  }
  
  private moveUp(distance: number): void {
    this.camera.position.y += distance;
  }
  
  private updateCollisions(): void {
    // Simple collision detection - check if we're on the ground
    if (this.camera.position.y <= this.playerHeight) {
      this.camera.position.y = this.playerHeight;
      this.collisions.bottom = true;
    } else {
      this.collisions.bottom = false;
    }
  }
} 